// frontend/src/components/AisaTable.jsx

import React, { useState, useMemo, useEffect } from "react";
import "./AisaTable.css";
import "../design-system.css";
import axios from 'axios'; // Assicura che axios sia importato

// Funzione helper per assegnare classi di larghezza in base all'etichetta
function getWidthClass(label) {
  const L = (label || "").toLowerCase();
  if (/\b(id|cvid|età|eta|voto|score|anni|tel|fitting %|email)\b/i.test(L)) return "col-narrow";
  if (/(motivazione|profilo|descriz|esperienz|competenze|note|incarichi)/i.test(L)) return "col-wide";
  return "col-medium";
}

export default function AisaTable({ columns = [], data = [], rowIds = [], footerContent = null }) {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });

  // --- SELECTION STATE ---
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  // Columns metadata mapping
  const colsMeta = useMemo(() => columns.map((col, index) => ({
    key: col.original_name, // Use original_name as unique key
    label: col.display_alias, // Use display_alias for label
    index: index, // Store original index to access data array
    widthClass: getWidthClass(col.display_alias),
  })), [columns]);

  // Find CVID Column Index (Fallback if rowIds not provided)
  const cvidColIndex = useMemo(() => {
    const found = colsMeta.find(c => c.key.toLowerCase() === 'cvid' || c.key.toLowerCase() === 'id');
    return found ? found.index : 0;
  }, [colsMeta]);

  // --- DATA PREPARATION (Merge Data + IDs) ---
  const combinedData = useMemo(() => {
    return data.map((row, index) => ({
      cells: row,
      id: (rowIds && rowIds[index]) ? String(rowIds[index]) : (row[cvidColIndex] ? String(row[cvidColIndex]) : `row-${index}`)
    }));
  }, [data, rowIds, cvidColIndex]);

  // Sorting Logic (Operates on combinedData)
  const sortedData = useMemo(() => {
    let sortableItems = [...combinedData];
    if (sortConfig.key !== null) {
      const colIndex = colsMeta.find(c => c.key === sortConfig.key)?.index;
      if (colIndex !== undefined) {
        sortableItems.sort((aItem, bItem) => {
          let aVal = aItem.cells[colIndex];
          let bVal = bItem.cells[colIndex];
          const aNum = parseFloat(aVal);
          const bNum = parseFloat(bVal);
          const isNum = (val) => /^-?\d*(\.\d+)?$/.test(val) && val !== '';

          if (isNum(aVal) && isNum(bVal)) {
            aVal = aNum;
            bVal = bNum;
          } else {
            aVal = (aVal || '').toString().toLowerCase();
            bVal = (bVal || '').toString().toLowerCase();
          }

          if (aVal < bVal) return sortConfig.direction === 'ascending' ? -1 : 1;
          if (aVal > bVal) return sortConfig.direction === 'ascending' ? 1 : -1;
          return 0;
        });
      }
    }
    return sortableItems;
  }, [combinedData, sortConfig, colsMeta]);

  // --- SORTING HANDLERS ---
  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'ascending' ? ' ▲' : ' ▼';
  };

  // --- SELECTION LOGIC ---
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = new Set(sortedData.map(item => item.id));
      setSelectedIds(allIds);
    } else {
      setSelectedIds(new Set());
    }
  };

  const handleSelectRow = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  useEffect(() => {
    setShowBulkActions(selectedIds.size > 0);
  }, [selectedIds]);

  // --- BULK ACTION HANDLERS ---
  const handleDownloadZip = async () => {
    try {
      const ids = Array.from(selectedIds);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/export/zip_cvs', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ cvids: ids })
      });
      if (!response.ok) throw new Error("Errore scaricamento ZIP");
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `cv_export_${new Date().toISOString().slice(0, 10)}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
      alert("Errore durante il download dello ZIP: " + err.message);
    }
  };

  const handleDownloadXlsx = async () => {
    try {
      const ids = Array.from(selectedIds);
      if (ids.length === 0) {
        alert("Nessun candidato selezionato");
        return;
      }

      // Recupera i dati ESATTI visualizzati in tabella (WYSIWYG)
      // 1. Filtra solo le righe selezionate
      const selectedRows = sortedData.filter(item => selectedIds.has(item.id));

      // 2. Prepara le colonne (Alias visualizzati), escludendo "Azioni"
      // Trova gli indici delle colonne da mantenere
      const columnsToExportIndices = colsMeta
        .map((c, idx) => ({ ...c, originalIndex: idx }))
        .filter(c => c.label !== "Azioni" && c.key !== "actions")
        .map(c => c.originalIndex);

      const exportColumns = columnsToExportIndices.map(idx => colsMeta[idx].label);

      // 3. Prepara le righe (Dati celle) filtrando le celle corrispondenti
      const exportRows = selectedRows.map(item =>
        columnsToExportIndices.map(idx => item.cells[idx])
      );

      const token = localStorage.getItem('token');

      const payload = {
        columns: exportColumns,
        rows: exportRows,
        query: `Export Selezione ${new Date().toLocaleDateString()}`
      };

      const response = await fetch('/api/export/xlsx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Errore export Excel");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_selection_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (err) {
      console.error(err);
      alert("Errore durante l'export Excel: " + err.message);
    }
  };

  if (!columns?.length || !data?.length) {
    return (
      <div className="table-scroll-wrapper empty">
        <div className="empty-cell">Nessun dato da visualizzare</div>
      </div>
    );
  }

  const allSelected = sortedData.length > 0 && selectedIds.size === sortedData.length;

  return (
    <div className="table-container-outer">
      {/* BULK ACTIONS BAR */}
      {showBulkActions && (
        <div className="bulk-actions-bar" style={{
          padding: '10px 15px', background: 'var(--ds-accent-bg)', borderBottom: '0.5px solid var(--ds-border)',
          display: 'flex', alignItems: 'center', gap: '12px'
        }}>
          <span style={{ fontWeight: 600, color: 'var(--ds-accent-txt)' }}>
            {selectedIds.size} candidati selezionati
          </span>
          <button onClick={handleDownloadZip} className="ds-btn ds-btn-primary" style={{ gap: '5px' }}>
            📂 Scarica ZIP CV
          </button>
          <button onClick={handleDownloadXlsx} className="ds-btn" style={{ gap: '5px' }}>
            📊 Esporta Excel
          </button>
          <button onClick={() => setSelectedIds(new Set())} className="ds-btn ds-btn-ghost" style={{ marginLeft: 'auto' }}>
            Annulla
          </button>
        </div>
      )}

      <div className="table-scroll-wrapper">
        <table className="message-table">
          <thead>
            <tr>
              <th style={{ width: '40px', textAlign: 'center' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </th>
              {colsMeta.map((c) => (
                <th
                  key={c.key}
                  className={`${c.widthClass} sortable-header`}
                  onClick={() => requestSort(c.key)}
                  style={{ cursor: 'pointer', position: 'relative' }}
                  title="Clicca per ordinare"
                >
                  {c.label}
                  <span className="sort-indicator">{getSortIndicator(c.key)}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedData.map((item, rIdx) => {
              const rowId = item.id;
              const isSelected = selectedIds.has(rowId);
              const row = item.cells;
              return (
                <tr key={rIdx} className={isSelected ? 'row-selected' : ''}>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelectRow(rowId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  {(Array.isArray(row) ? row : []).map((cell, cIdx) => (
                    <td key={cIdx} className={colsMeta[cIdx]?.widthClass || ''}>
                      <div className="cell-scroll">
                        {cell}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
          {footerContent && (
            <tfoot>
              <tr>
                <td colSpan="100%" style={{ position: 'sticky', bottom: 0, backgroundColor: 'var(--ds-surface-2)', borderTop: '0.5px solid var(--ds-border)', padding: '10px', zIndex: 5 }}>
                  {footerContent}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
